import {
  Bar, BarChart, CartesianGrid, ErrorBar, PolarAngleAxis,
  RadialBar, RadialBarChart, XAxis, YAxis,
} from 'recharts';

import { ChartContainer, ChartTooltip, ChartTooltipContent, type ChartConfig } from '@/components/ui/chart';
import { useDimensionLabel, useNumber, useOptionalT, usePercent, useT } from '@/lib/i18n';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCaption, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { usePersonaName } from '@/hooks/use-persona-name';
import { useBandLabel } from '@/features/result/labels';

/**
 * THE SECOND DATA-DRIVEN CORE (build/05 §"The two data-driven cores").
 *
 * `VIZ` is a `Record<VizHint, FC>` and `ResultView` renders
 * `VIZ[envelope.visualization]`. There is no `if (model === 'RIASEC')` anywhere
 * in the console — PRD FR-R1 requires exactly that, and it is why
 * `visualization` had to be added to the contract in Phase 2.
 *
 * EVERY CHART HERE DRAWS THE `display` PROJECTION, NOT THE TYPED BLOCK. That is
 * contract fix C1: `display` is the render-agnostic `{key,label,value,band}[]`
 * carried by every score, so a consumer can draw a model it has never seen. A
 * radar that read `score.dimensions.R` would work for RIASEC and break on the
 * fourth model; reading `display` cannot.
 *
 * COLOUR (dataviz skill, step 2). Every chart here is SINGLE-SERIES — one
 * candidate's profile across dimensions of one measure — so no categorical
 * palette is involved and no adjacent-pair CVD validation applies: there are no
 * pairs. One hue, `--chart-1`, taken from the design system unchanged so the
 * charts re-skin with the console's eight presets and dark mode rather than
 * pinning hexes that would be right in one theme and wrong in seven.
 *
 * Band is a STATUS, and status is never colour alone (skill non-negotiable): the
 * band label is always rendered as text beside the value.
 */

export interface DisplayDimension {
  key: string;
  label: { en: string; ar: string };
  value: number;
  band: string;
  /** N3 — 0..100 against `VizProps.normGroup`. Absent means NO norm covered it. */
  percentile?: number;
  /**
   * N4 — an interval around `value`, in the same display units as `value`, that
   * names its CONSTRUCTION (`basis`) rather than a coverage level it cannot
   * claim. Never called a confidence interval on screen: that word is a coverage
   * claim, and a reader assuming 95% over-reads a standard-error band twofold.
   */
  interval?: { low: number; high: number; basis: string };
}

export interface VizProps {
  headline: string;
  dimensions: DisplayDimension[];
  /** N3 — the persona whose norm table produced the percentiles, if any did. */
  normGroup?: string;
}

/* --------------------------------------------------- the reference group (N3) */

/**
 * "High compared to whom?" — the first question anyone asks of a score, and the
 * one a counsellor has to be able to answer out loud.
 *
 * A percentile with no cohort named is the same non-answer as no percentile at
 * all, so the two are rendered together or not at all. `normGroup` is a persona
 * id (`ScoringConfig.norms` is keyed by persona), which is a database key and
 * not a cohort a person recognises — `usePersonaName` is what turns it into the
 * name the operator already reads on every other screen.
 *
 * Returns `null` when no dimension carries a percentile, which is every result
 * from a module with no norm table: **nothing is rendered in its place**
 * (decision D-PP2). Calibrating a norm is deferred by PRD §6.5; a screen that
 * said "not available" would be reporting the deferral as a finding about the
 * candidate.
 */
function useNormGroupNote(): (dimensions: DisplayDimension[], normGroup?: string) => string | null {
  const t = useT();
  const personaName = usePersonaName();

  return (dimensions, normGroup) => {
    if (!dimensions.some((d) => d.percentile !== undefined)) return null;
    // The server omits `normGroup` unless a table actually produced something,
    // so the unnamed branch is a stack older than N3 rather than a lie waiting
    // to happen. Say what is true in each case and claim nothing extra.
    return normGroup
      ? t('result.percentile.cohort', { cohort: personaName(normGroup) })
      : t('result.percentile.cohortUnnamed');
  };
}

/* ------------------------------------------------------- the interval (N4) */

/**
 * The interval as recharts' `ErrorBar` wants it: distance BELOW and ABOVE the
 * value, not the endpoints.
 *
 * Exported because it is the whole encoding — a test can assert the arithmetic
 * without a browser, which is the only way to assert it at all: recharts
 * measures its container, and jsdom has no layout, so the chart renders nothing
 * in a test. Clamped at zero so an interval that does not bracket its own value
 * cannot draw a whisker pointing the wrong way.
 *
 * **`undefined` when there is no interval, never `[0, 0]`** — recharts skips a
 * falsy error value but would happily draw a zero-width cap, and a tick mark
 * that says "measured exactly" is worse than no mark at all.
 */
export function intervalOffsets(dimension: DisplayDimension): [number, number] | undefined {
  const { interval, value } = dimension;
  if (!interval) return undefined;
  return [Math.max(0, value - interval.low), Math.max(0, interval.high - value)];
}

/**
 * What to CALL the interval — from `basis`, with the usual three steps down.
 *
 * The band has to be legible as the thing it is. `basis` names the construction
 * (`standard_error`), and the console owns the words: a column header and a
 * sentence, both bilingual, both looked up as `result.interval.*.<basis>`.
 *
 * **A basis this catalogue has never seen names NO basis** — the generic header
 * and a sentence that describes a precision range without saying how it was
 * built. Printing `standard_error` at a reader is the outcome this avoids, and
 * inventing a claim about a construction we do not recognise is the other one.
 * The same three steps `useDimensionLabel` and `useHeadline` take.
 *
 * Mixed bases across one projection collapse to the unnamed form too. One
 * caption cannot honestly name two constructions, and a model that mixes them is
 * a case nobody has designed for yet.
 */
function useIntervalWords(): (dimensions: DisplayDimension[]) => { header: string; hint: string } {
  const t = useT();
  const optionalT = useOptionalT();

  return (dimensions) => {
    const bases = new Set(dimensions.flatMap((d) => (d.interval ? [d.interval.basis] : [])));
    const basis = bases.size === 1 ? [...bases][0] : undefined;

    return {
      header: (basis && optionalT(`result.interval.header.${basis}`)) ?? t('result.interval.header'),
      hint: (basis && optionalT(`result.interval.hint.${basis}`)) ?? t('result.interval.hint'),
    };
  };
}

/**
 * WHICH LANGUAGE A RESULT IS READ IN — and why it is NOT `administeredLocale`.
 *
 * PROGRESS §28c left this as a carry-forward phrased as "the result view still
 * renders English-first, without consulting the envelope's `administeredLocale`
 * — the same shape as the harness bug". H1 makes that the wrong fix, and the
 * distinction is worth stating because the two cases look alike:
 *
 *   - **The harness** shows a CANDIDATE the items. What they are shown is the
 *     administration, so it must follow the instance's pinned locale.
 *   - **This screen** shows an ANALYST a finished envelope. Nothing here is
 *     administered to anyone; it is a report being read. So it follows the
 *     reader's own language, like every other console screen.
 *
 * `administeredLocale` remains visible as a FACT, on the Provenance card, which
 * is what reproducibility metadata is for. Rendering an Arabic-administered
 * result's labels in Arabic to an English-reading analyst would be applying the
 * candidate's language to the reviewer — the mirror image of the §28c defect.
 */
function useChartData(): (dimensions: DisplayDimension[]) => Array<{
  name: string; value: number; band: string; error?: [number, number];
}> {
  const dimensionLabel = useDimensionLabel();
  return (dimensions) =>
    dimensions.map((d) => ({
      name: dimensionLabel(d.key, d.label),
      value: d.value,
      band: d.band,
      error: intervalOffsets(d),
    }));
}

/** Inside a hook, because the series label is now a translated string. */
function useChartConfig(): ChartConfig {
  const t = useT();
  return { value: { label: t('result.score'), color: 'var(--chart-1)' } } satisfies ChartConfig;
}

/* ------------------------------------------------------------------- radar */

/**
 * Dimensions on their own axes, where the shape is the point.
 *
 * ### Why this stopped being a recharts chart
 *
 * It was one, and its labels were **clipped on the deployed console** — reported
 * from a screenshot showing `ventional`, `erprising` and `Investigo`. Recharts
 * defaults `outerRadius` to 80%, leaving a fifth of the half-dimension for a
 * polar tick label; a word needs about three times that.
 *
 * §34q's carry-forward said this defect was latent and left it, on the reasoning
 * that *"nothing emits `radar` any more"* — RIASEC picks `hexagon` whenever it
 * scored six dimensions. **That reasoning was wrong in the way that matters.**
 * Nothing emits it for a *new* result; every result scored before that change
 * still carries `visualization: 'radar'` in its stored envelope, and a stored
 * envelope is served verbatim and never recomputed (`02 §1`). So the fallback is
 * not dead — it is what the entire existing corpus of results renders with, and
 * it was the only chart anybody actually had on screen.
 *
 * Hand-rolled for the reason the hexagon was: SVG text cannot be measured
 * without layout, jsdom has none, and a chart whose geometry is arithmetic can
 * be *asserted*. It now shares `vertexAt` / `labelAt` with the hexagon, so both
 * are covered by the same label-budget test.
 *
 * ### Rings, not a polygon frame — and the difference is the whole distinction
 *
 * The hexagon draws straight edges between neighbouring vertices because for a
 * cyclic model adjacency *means* something. A radar's dimensions have no such
 * relation: they are independent magnitudes that happen to be drawn round a
 * circle. Concentric **rings** say that, and a polygon frame would assert the
 * adjacency the hexagon exists to assert. Same geometry, deliberately different
 * frame.
 *
 * NO INTERVAL IS DRAWN HERE, unchanged and for the same reason: an interval on a
 * radar is a second and third polygon, and three overlapping closed shapes read
 * as three profiles rather than as one profile's precision. The table below
 * carries it — a chart may not be the only carrier of information.
 */
function RadarViz({ dimensions }: VizProps) {
  const t = useT();
  const dimensionLabel = useDimensionLabel();
  const count = dimensions.length;

  // Below three axes there is no area to enclose, and two spokes read as a line.
  if (count < 3) return null;

  const centre = { x: POLAR_WIDTH / 2, y: POLAR_HEIGHT / 2 };

  return (
    <svg
      viewBox={`0 0 ${POLAR_WIDTH} ${POLAR_HEIGHT}`}
      className="mx-auto block max-h-80 w-full"
      role="img"
      aria-label={t('result.radar.label')}
    >
      {/* Recessive grid; the data mark carries the ink. Quarter rings, so a
          reader can place a vertex without counting pixels. */}
      {[0.25, 0.5, 0.75, 1].map((fraction) => (
        <circle
          key={fraction}
          cx={centre.x}
          cy={centre.y}
          r={POLAR_RADIUS * fraction}
          fill="none"
          stroke="var(--border)"
          strokeWidth={fraction === 1 ? 1.5 : 1}
        />
      ))}

      {dimensions.map((d, i) => {
        const outer = vertexAt(i, count, 1);
        return (
          <line
            key={d.key}
            x1={centre.x}
            y1={centre.y}
            x2={outer.x}
            y2={outer.y}
            stroke="var(--border)"
            strokeWidth={1}
          />
        );
      })}

      <polygon
        points={polygon(count, (i) => (dimensions[i]!.value ?? 0) / 100)}
        fill="var(--chart-1)"
        fillOpacity={0.25}
        stroke="var(--chart-1)"
        strokeWidth={2}
        strokeLinejoin="round"
      />

      {dimensions.map((d, i) => {
        const point = vertexAt(i, count, (d.value ?? 0) / 100);
        const label = labelAt(i, count);
        return (
          <g key={d.key}>
            {/* r=4 → an 8px marker, the floor for a hit target and for a mark
                that has to stay visible where the fill sits behind it. */}
            <circle cx={point.x} cy={point.y} r={4} fill="var(--chart-1)" />
            <text
              x={label.x}
              y={label.y}
              textAnchor={label.anchor}
              dominantBaseline="middle"
              // Geometric left/right in both languages — see `labelAt`.
              direction="ltr"
              className="fill-muted-foreground text-[11px]"
            >
              {dimensionLabel(d.key, d.label)}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

/* -------------------------------------------------------------------- bars */

/**
 * Five traits, magnitude compared across a common 0–100 scale — and the one
 * chart here that can carry an interval honestly (N4).
 *
 * ### What is drawn, and what is emphatically not
 *
 * `dimensions[].interval` and only that. It arrives in DISPLAY UNITS, the same
 * 0–100 scale as `value`, from the estimator that produced `value`.
 * `score.skills[].standardError` is SE(θ) on the logit scale and describes a
 * different estimator entirely; drawing one on the other would invent a
 * relationship between two things — and every number would still look plausible,
 * which is what makes it worth a paragraph. SE(θ) is rendered in the technical
 * read, labelled with its scale.
 *
 * ### A band, never a second number
 *
 * The interval is a whisker on the mark. A point estimate drawn as a solid bar
 * is what invites the over-interpretation N4 exists to prevent, and a numeric
 * "±4" beside the score would invite it again in a different font: it reads as
 * more precision, not less. The number IS available — in the table below, where
 * a reader looking for it is looking at a list of figures already.
 *
 * Absent interval ⇒ the bar as it has always been. `intervalOffsets` returns
 * `undefined`, recharts skips the mark, and the whisker layer is not mounted at
 * all when nothing carries one.
 */
function BarsViz({ dimensions }: VizProps) {
  const chartData = useChartData();
  const config = useChartConfig();
  const anyInterval = dimensions.some((d) => d.interval !== undefined);

  return (
    <ChartContainer config={config} className="max-h-80 w-full">
      <BarChart data={chartData(dimensions)} layout="vertical" margin={{ left: 8, right: 16 }}>
        <CartesianGrid horizontal={false} stroke="var(--border)" />
        <XAxis
          type="number"
          domain={[0, 100]}
          tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
        />
        <YAxis
          type="category"
          dataKey="name"
          width={140}
          tickLine={false}
          axisLine={false}
          tick={{ fill: 'var(--muted-foreground)', fontSize: 12 }}
        />
        <ChartTooltip content={<ChartTooltipContent />} />
        {/* 4px rounded data-end, anchored to the baseline. */}
        <Bar dataKey="value" fill="var(--color-value)" radius={[0, 4, 4, 0]} barSize={18}>
          {anyInterval && (
            /* Annotation ink, not the series colour (dataviz: text and
               annotations wear text tokens) — and it has to stay legible where
               it crosses the bar's own fill, which `--muted-foreground` would
               not in every one of the console's eight presets. `direction="x"`
               because the layout is vertical, so the value axis is x. */
            <ErrorBar
              dataKey="error"
              direction="x"
              width={5}
              strokeWidth={1.5}
              stroke="var(--foreground)"
            />
          )}
        </Bar>
      </BarChart>
    </ChartContainer>
  );
}

/* ------------------------------------------------------------------- gauge */

/**
 * One dimension per gauge. Recharts' radial bar needs an explicit 0–100 domain,
 * or it scales each arc to its own maximum and every dimension looks full.
 *
 * **The interval is not drawn on the arc, and that is a limitation rather than a
 * decision to be proud of.** A radial band means either a second concentric ring
 * — which reads as a second measure, not as one measure's precision — or an
 * overlapping sector whose geometry cannot be checked without looking at it, and
 * this console is developed against a remote stack with no local render. So the
 * gauge shows the point estimate, and the "All dimensions" table below it (which
 * is always present under a non-table chart) carries the interval in full. If
 * the radial band is worth building, build it against a rendered page, not a
 * type-checked one.
 */
function GaugeViz({ dimensions, normGroup }: VizProps) {
  const config = useChartConfig();
  const dimensionLabel = useDimensionLabel();
  const bandLabel = useBandLabel();
  const t = useT();
  const percent = usePercent();
  const normGroupNote = useNormGroupNote();
  const note = normGroupNote(dimensions, normGroup);

  return (
    <div className="space-y-3">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {dimensions.map((d) => (
          <div key={d.key} className="rounded-lg border p-4 text-center">
            <ChartContainer config={config} className="mx-auto aspect-square max-h-40">
              <RadialBarChart
                data={[{ name: dimensionLabel(d.key, d.label), value: d.value }]}
                startAngle={210}
                endAngle={-30}
                innerRadius="70%"
                outerRadius="100%"
              >
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} axisLine={false} />
                <RadialBar dataKey="value" fill="var(--color-value)" cornerRadius={4} background />
              </RadialBarChart>
            </ChartContainer>
            {/* The hero number, in text ink rather than the series colour. */}
            <p className="text-2xl font-semibold">{d.value}</p>
            <p className="text-sm font-medium">{dimensionLabel(d.key, d.label)}</p>
            <Badge variant="secondary" className="mt-1">{bandLabel(d.band)}</Badge>
            {/* N3 — beside the band, and ONLY when a norm covered this
                dimension. A dimension the norm table does not reach renders its
                band alone, exactly as it did before N3: no dash, no "not
                available", no greyed zero (decision D-PP2). A missing norm is a
                fact about the module's calibration, not about the candidate. */}
            {d.percentile !== undefined && (
              <p className="text-muted-foreground mt-1 text-xs">
                {t('result.percentile.short', { value: percent(d.percentile) })}
              </p>
            )}
          </div>
        ))}
      </div>

      {/* Named ONCE for the whole grid rather than on every card: the cohort is a
          property of the norm table, not of a dimension, and twelve repetitions
          of the same clause is noise a reader learns to skip. */}
      {note && <p className="text-muted-foreground text-xs">{note}</p>}
    </div>
  );
}

/* ----------------------------------------------------------------- profile */

/**
 * The middle half of the reference group — 25th to 75th percentile.
 *
 * Not a threshold and not a target: it is where half the cohort sits, and its
 * only job is to make "above average" and "unusual" visually different. Drawn as
 * a shaded stripe behind every row so the comparison is one glance rather than a
 * column of numbers.
 */
const TYPICAL_LOW = 25;
const TYPICAL_HIGH = 75;

/**
 * THE NORM-REFERENCED READ — where each dimension sits against its cohort.
 *
 * The chart Big Five reports have used for decades, and the one this console
 * could not draw until there was an editor that could author a norm table. A
 * bare 0–100 bar answers "how much" and a counsellor's actual question is "how
 * much *compared to whom*" — which is why `normGroup` is named on the axis
 * caption rather than in a tooltip.
 *
 * ### The axis is PERCENTILE, so the interval is not drawn
 *
 * `dimensions[].interval` is in display units — the 0–100 score scale — and this
 * axis is a percentile. Drawing one on the other would be the same error as
 * putting SE(θ) on a normalised bar, which `BarsViz` above spends a paragraph
 * refusing: two scales, one picture, every number still plausible. Converting
 * the endpoints would need the norm's mean and SD, which live in the module
 * config and are deliberately not on the envelope. So the whisker is absent
 * here and the "All dimensions" table below carries the interval in full, which
 * is this console's standing rule for anything a chart cannot say honestly.
 *
 * ### A dimension with no percentile draws an EMPTY track
 *
 * Not a marker at zero, and not a hidden row. D-PP2: a missing norm is a fact
 * about the module's calibration, and dropping the row would hide a dimension
 * the candidate was actually scored on. The row keeps its label and its band —
 * which is exactly what the result view shows when there are no norms at all.
 *
 * Built from CSS rather than recharts, and that is deliberate: recharts measures
 * its container, jsdom has no layout, and this console is developed against a
 * remote stack. A chart whose geometry is percentage widths can be asserted in a
 * test; one that renders nothing without a browser cannot.
 */
function ProfileViz({ dimensions, normGroup }: VizProps) {
  const t = useT();
  const percent = usePercent();
  const dimensionLabel = useDimensionLabel();
  const bandLabel = useBandLabel();
  const normGroupNote = useNormGroupNote();
  const note = normGroupNote(dimensions, normGroup);

  // A profile with nothing to plot is a bar chart with extra scenery. The
  // plugins pick this hint from the data and only emit it when a norm applied,
  // so this is the hand-crafted-envelope path — degrade to the chart that can
  // draw what IS present rather than to an empty frame.
  if (!dimensions.some((d) => d.percentile !== undefined)) {
    return <BarsViz dimensions={dimensions} headline="" normGroup={normGroup} />;
  }

  return (
    <div className="space-y-3">
      <div className="space-y-2">
        {dimensions.map((d) => (
          <div key={d.key} className="grid grid-cols-[minmax(6rem,10rem)_1fr] items-center gap-3">
            {/* `truncate` is a deliberate ellipsis, not a clip — the column is
                `minmax(6rem,10rem)` and a long dimension name will not fit on a
                narrow screen. `title` is what keeps the full text reachable, and
                the table below carries it in full regardless. */}
            <div className="min-w-0">
              <p className="truncate text-sm font-medium" title={dimensionLabel(d.key, d.label)}>
                {dimensionLabel(d.key, d.label)}
              </p>
              <p className="text-muted-foreground truncate text-xs">{bandLabel(d.band)}</p>
            </div>

            {/* LTR because the track is a number line — the same treatment an id
                and a version string get. Flipping it per language would mean an
                Arabic reader and an English one disagree about which end is
                "high", for a scale that has no language. */}
            <div className="relative h-7" dir="ltr">
              <div className="bg-muted absolute inset-x-0 inset-y-2 rounded-full" />
              {/* The middle half of the cohort. */}
              <div
                className="bg-muted-foreground/20 absolute inset-y-1 rounded-sm"
                style={{
                  insetInlineStart: `${TYPICAL_LOW}%`,
                  width: `${TYPICAL_HIGH - TYPICAL_LOW}%`,
                }}
                aria-hidden
              />
              {/* The median, which is what "typical" is centred on. */}
              <div
                className="bg-border absolute inset-y-1 w-px"
                style={{ insetInlineStart: '50%' }}
                aria-hidden
              />

              {d.percentile !== undefined && (
                <div
                  className="absolute top-1/2 size-3 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2"
                  style={{
                    insetInlineStart: `${d.percentile}%`,
                    background: 'var(--chart-1)',
                    // Rings the mark in the page's own background so it stays
                    // legible where it lands on top of the shaded stripe.
                    ['--tw-ring-color' as string]: 'var(--background)',
                  }}
                  role="img"
                  aria-label={t('result.percentile.short', { value: percent(d.percentile) })}
                />
              )}
            </div>
          </div>
        ))}
      </div>

      {/* The axis, stated once. Three numbers rather than a rule of ticks: the
          only positions a reader needs to locate are the ends and the middle. */}
      <div className="text-muted-foreground grid grid-cols-[minmax(6rem,10rem)_1fr] gap-3 text-xs">
        <span />
        <span className="flex justify-between" dir="ltr">
          <span>{percent(0)}</span>
          <span>{percent(50)}</span>
          <span>{percent(100)}</span>
        </span>
      </div>

      <p className="text-muted-foreground text-xs">
        {t('result.profile.typical')} {note}
      </p>
    </div>
  );
}

/* ----------------------------------------------------------------- hexagon */

const POLAR_RADIUS = 90;

/**
 * THE FRAME IS WIDER THAN IT IS TALL, AND THE ASYMMETRY IS THE WHOLE POINT.
 *
 * The first version used one padding for both axes and clipped four of its six
 * labels. The arithmetic says why, and it is worth keeping: a side vertex sits
 * at `cx ± 78`, its label anchors at `cx ± 90`, and a square 236-wide box left
 * **27 units** for a word like "Investigative" that needs about 70. The figure
 * looked right and the labels ran off the edge.
 *
 * Labels leave a vertex travelling OUTWARD, so the room each one needs depends
 * on which way it points. The top and bottom labels need one line of height;
 * the side labels need a word's width. Padding them equally either clips the
 * sides or wastes a third of the height.
 *
 * `POLAR_PAD_X` is budgeted for the WORST case rather than for a hexagon: a
 * figure with an even number of vertices puts one at full radius on the x-axis,
 * where a hexagon's widest reaches only `cos 30°` ≈ 0.87 of it. An eight-sided
 * model is the extensibility case this component claims to support, and sizing
 * the box to a hexagon would have clipped it — which is what the test asserting
 * this over 3, 6 and 8 vertices found the first time it ran.
 *
 * The extra width is free in practice: the render is height-capped by
 * `max-h-80`, so a wider viewBox pads the sides rather than shrinking the figure.
 */
const POLAR_PAD_Y = 26;
const POLAR_PAD_X = 96;
/** Exported so the label budget below can be asserted against the actual box. */
export const POLAR_WIDTH = (POLAR_RADIUS + POLAR_PAD_X) * 2;
export const POLAR_HEIGHT = (POLAR_RADIUS + POLAR_PAD_Y) * 2;

/** How far a label sits beyond its vertex, as a fraction of the radius. */
const LABEL_OFFSET = 0.16;

/**
 * Room reserved for the longest side label, in viewBox units.
 *
 * A budget rather than a measurement: SVG text cannot be measured without
 * layout, this console is developed against a remote stack, and jsdom has no
 * layout at all — so the guarantee has to be arithmetic. At `font-size: 11` a
 * Latin character averages ~5.5 units, which covers about 15 — past
 * "Investigative" (13) and "Conventional" (12). Arabic in Cairo is narrower per
 * glyph at the same size.
 *
 * Exported so `viz-shapes.test.tsx` can assert the box actually holds it. A
 * budget nothing checks is how the first version clipped.
 */
export const POLAR_LABEL_BUDGET = 80;

/**
 * A vertex position at `fraction` of full radius, starting at the top and going
 * clockwise. Exported for the same reason `intervalOffsets` is: the geometry IS
 * the chart, and a test can check arithmetic where it cannot check a rendering.
 */
export function vertexAt(index: number, count: number, fraction: number): { x: number; y: number } {
  const angle = -Math.PI / 2 + (index / count) * Math.PI * 2;
  const radius = POLAR_RADIUS * Math.min(Math.max(fraction, 0), 1);
  return {
    x: POLAR_WIDTH / 2 + radius * Math.cos(angle),
    y: POLAR_HEIGHT / 2 + radius * Math.sin(angle),
  };
}

/**
 * Where a label sits, and which edge of it meets that point.
 *
 * ### `start`/`end` are LOGICAL, and that is an RTL bug waiting to happen
 *
 * SVG's `text-anchor` resolves against the inline direction, so under
 * `direction: rtl` a `start` anchor pins the text's RIGHT edge. Every label on
 * the right of the figure would then extend leftward back across the chart, and
 * every label on the left would run off the edge — in Arabic only, on a screen
 * nobody had opened in Arabic yet.
 *
 * So the `<text>` carries `direction="ltr"` and these anchors mean geometric
 * left and right in both languages. A label is a single run, and bidi renders
 * an Arabic run correctly inside an LTR context, so nothing about the Arabic is
 * harmed. It is the same call `ProfileViz` makes for its percentile track and
 * the Provenance card makes for an id: the figure is geometry, not prose, and
 * mirroring it would only mean an Arabic reader and an English one disagree
 * about which vertex is which.
 */
export function labelAt(index: number, count: number): {
  x: number;
  y: number;
  anchor: 'start' | 'middle' | 'end';
} {
  const vertex = vertexAt(index, count, 1);
  const dx = vertex.x - POLAR_WIDTH / 2;
  const dy = vertex.y - POLAR_HEIGHT / 2;
  return {
    x: vertex.x + dx * LABEL_OFFSET,
    y: vertex.y + dy * LABEL_OFFSET,
    // A vertex on the vertical axis has no side to sit on and is centred over
    // the point; the tolerance keeps floating-point dust from picking a side.
    anchor: Math.abs(dx) < 1 ? 'middle' : dx > 0 ? 'start' : 'end',
  };
}

const polygon = (count: number, fractionAt: (i: number) => number): string =>
  Array.from({ length: count }, (_, i) => {
    const { x, y } = vertexAt(i, count, fractionAt(i));
    return `${x.toFixed(2)},${y.toFixed(2)}`;
  }).join(' ');

/**
 * DIMENSIONS IN A CYCLE, WHERE ADJACENCY MEANS SOMETHING.
 *
 * The frame is the information. A radar draws six spokes over concentric rings,
 * which is a picture of six unrelated magnitudes — and for a model whose whole
 * interpretive apparatus is *which themes sit next to which*, the rings say
 * nothing and the reader cannot see from the chart why two adjacent peaks are a
 * coherent profile and two opposite ones are not. Drawing the frame as a closed
 * polygon with straight edges between neighbours puts the adjacency on the page.
 *
 * ### It knows nothing about any model
 *
 * It takes `display.dimensions` in the order the envelope carries them — which
 * is the config's order, which for a cyclic model IS the cycle — and draws an
 * n-gon. Six of them makes a hexagon. A model that ships eight cyclic
 * dimensions gets an octagon with no edit here, which is the same property
 * `RENDERERS` and the rest of `VIZ` have.
 *
 * ### The three highest are emphasised, and that is all "top three" means here
 *
 * Sorted by value, ties broken by position in `dimensions` — the same rule the
 * server's `ranked()` uses, and the reason it uses it: two identical response
 * sets must not produce two different emphases. No code is composed, no letters
 * are printed; the headline already carries whatever the model made of them.
 */
function HexagonViz({ dimensions }: VizProps) {
  const t = useT();
  const dimensionLabel = useDimensionLabel();
  const bandLabel = useBandLabel();
  const count = dimensions.length;

  /**
   * Below a triangle there is no polygon to draw and no adjacency to show.
   *
   * **`null`, not a `<TableViz>`.** Rendering the table here draws it TWICE:
   * `Result.tsx` already renders one under "All dimensions" for any chart that
   * is not itself the table, and it decides that by component identity — which a
   * chart degrading internally defeats. It cannot be taught the difference
   * either, because asking it would mean branching on the hint, and
   * `harness-viz.test.tsx` fails the build for exactly that.
   *
   * So the chart draws nothing and the table below carries everything, which is
   * the standing rule anyway. Defensive only: a plugin has to emit this hint,
   * and the one that does guards on having scored all six dimensions.
   */
  if (count < 3) return null;

  const top = new Set(
    dimensions
      .map((d, index) => ({ key: d.key, value: d.value, index }))
      .sort((a, b) => b.value - a.value || a.index - b.index)
      .slice(0, 3)
      .map((d) => d.key),
  );

  return (
    <div className="space-y-3">
      <svg
        viewBox={`0 0 ${POLAR_WIDTH} ${POLAR_HEIGHT}`}
        className="mx-auto block max-h-80 w-full"
        role="img"
        aria-label={t('result.hexagon.label')}
      >
        {/* The frame, and one inner ring at half scale so a reader can place a
            vertex without counting pixels. Recessive: the data mark carries the
            ink, which is the rule every other chart here follows. */}
        <polygon
          points={polygon(count, () => 1)}
          fill="none"
          stroke="var(--border)"
          strokeWidth={1.5}
        />
        <polygon points={polygon(count, () => 0.5)} fill="none" stroke="var(--border)" strokeWidth={1} />
        {/* Spokes, so each vertex is visibly a dimension's own axis rather than
            a corner of a shape. */}
        {dimensions.map((d, i) => {
          const outer = vertexAt(i, count, 1);
          return (
            <line
              key={d.key}
              x1={POLAR_WIDTH / 2}
              y1={POLAR_HEIGHT / 2}
              x2={outer.x}
              y2={outer.y}
              stroke="var(--border)"
              strokeWidth={1}
            />
          );
        })}

        {/* The profile. `value` is 0–100 by construction of the projection. */}
        <polygon
          points={polygon(count, (i) => (dimensions[i]!.value ?? 0) / 100)}
          fill="var(--chart-1)"
          fillOpacity={0.25}
          stroke="var(--chart-1)"
          strokeWidth={2}
          strokeLinejoin="round"
        />

        {dimensions.map((d, i) => {
          const point = vertexAt(i, count, (d.value ?? 0) / 100);
          // Position AND anchor from one place, so the room the viewBox reserves
          // and the room the labels actually use cannot drift apart.
          const label = labelAt(i, count);

          return (
            <g key={d.key}>
              <circle
                cx={point.x}
                cy={point.y}
                r={top.has(d.key) ? 5 : 3}
                fill="var(--chart-1)"
                // Emphasis is a SECOND channel on top of position, never the
                // only one: the ring is what survives when the fill and the
                // stroke are the same hue at a small radius.
                stroke={top.has(d.key) ? 'var(--background)' : 'none'}
                strokeWidth={top.has(d.key) ? 2 : 0}
              />
              <text
                x={label.x}
                y={label.y}
                textAnchor={label.anchor}
                dominantBaseline="middle"
                // Geometric left/right in both languages — `text-anchor` is
                // otherwise resolved against the inline direction, and in Arabic
                // every label would anchor by its opposite edge. See `labelAt`.
                direction="ltr"
                className="fill-muted-foreground text-[11px]"
              >
                {dimensionLabel(d.key, d.label)}
              </text>
            </g>
          );
        })}
      </svg>

      {/* A chart is never the only carrier. The three emphasised vertices are
          named in text, with their bands, because a bigger dot is not a
          readable statement and is invisible to a screen reader. */}
      <p className="text-muted-foreground text-center text-xs">
        {t('result.hexagon.top', {
          dimensions: dimensions
            .filter((d) => top.has(d.key))
            // `·` rather than a comma: the separator has to read the same in
            // both languages, and Arabic's comma is a different character that
            // would have to be chosen per locale for no gain.
            .map((d) => `${dimensionLabel(d.key, d.label)} (${bandLabel(d.band)})`)
            .join(' · '),
        })}
      </p>
    </div>
  );
}

/* ------------------------------------------------------------------- table */

/**
 * The universal fallback, and the accessible view of every other chart.
 *
 * Any `display` projection renders here, which is what lets a model with no
 * bespoke chart still be read — and satisfies the skill's "a table view exists"
 * accessibility requirement for the three that do have one.
 */
export function TableViz({ dimensions, normGroup }: VizProps) {
  const t = useT();
  const dimensionLabel = useDimensionLabel();
  const bandLabel = useBandLabel();
  const number = useNumber();
  const percent = usePercent();
  const normGroupNote = useNormGroupNote();
  const intervalWords = useIntervalWords();

  /**
   * A column appears only if the envelope has something to put in it.
   *
   * Not cosmetics: an empty "Percentile" column on every un-normed result would
   * make the absence of a norm table look like a gap in this candidate's data,
   * repeated once per row. Nothing is what a module with no norms should render
   * (D-PP2) — and where SOME dimensions are normed and others are not, the
   * column exists and the un-normed cells are genuinely empty.
   */
  const anyPercentile = dimensions.some((d) => d.percentile !== undefined);
  const anyInterval = dimensions.some((d) => d.interval !== undefined);
  // The column's name and the sentence under the table both come from `basis`,
  // so the band is legible as the thing it is rather than as "an interval".
  const words = intervalWords(dimensions);

  const note = [normGroupNote(dimensions, normGroup), anyInterval ? words.hint : null]
    .filter(Boolean)
    .join(' ');

  return (
    <Table>
      {/* The table is where the chart's marks become readable, so the two things
          a mark cannot say — which cohort, and what the whisker means — are said
          here rather than in a tooltip nobody hovers. */}
      {note && <TableCaption>{note}</TableCaption>}
      <TableHeader>
        <TableRow>
          <TableHead>{t('result.dimension')}</TableHead>
          <TableHead className="w-24">{t('result.score')}</TableHead>
          {anyInterval && <TableHead className="w-36">{words.header}</TableHead>}
          <TableHead className="w-32">{t('result.band')}</TableHead>
          {anyPercentile && <TableHead className="w-28">{t('result.percentile')}</TableHead>}
        </TableRow>
      </TableHeader>
      <TableBody>
        {dimensions.map((d) => {
          return (
            <TableRow key={d.key}>
              {/* One language, the reader's. The English underneath went with the
                  rule that a VIEW shows the operator's language and only an
                  EDITOR shows the pair — and here it was doubly redundant, since
                  the envelope's `ar` is a copy of its `en` by design. */}
              <TableCell>{dimensionLabel(d.key, d.label)}</TableCell>
              <TableCell className="font-medium">{d.value}</TableCell>
              {/* N4 — the interval as a RANGE, which is what a table can carry:
                  the chart draws it and this states it. `dir="ltr"` because the
                  cell is two numerals and a dash, the same treatment a version
                  string gets on the Provenance card. */}
              {anyInterval && (
                <TableCell className="text-muted-foreground" dir="ltr">
                  {d.interval &&
                    t('result.interval.range', {
                      low: number(d.interval.low),
                      high: number(d.interval.high),
                    })}
                </TableCell>
              )}
              <TableCell>
                <Badge variant="secondary">{bandLabel(d.band)}</Badge>
              </TableCell>
              {/* N3 — and an un-normed dimension's cell stays EMPTY. Not "—",
                  not "n/a", not 0. */}
              {anyPercentile && (
                <TableCell>
                  {d.percentile !== undefined && percent(d.percentile)}
                </TableCell>
              )}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

/** The map. A new viz hint is one entry; nothing else in the console changes. */
export const VIZ: Record<string, React.FC<VizProps>> = {
  radar: RadarViz,
  bars: BarsViz,
  gauge: GaugeViz,
  table: TableViz,
  profile: ProfileViz,
  hexagon: HexagonViz,
};
